/**
 * Generation History Types (Shared)
 *
 * Wire types for generation history read from the `generations` table.
 * Used by both web and mobile clients for cross-device sync.
 */

import type { AnalysisMode } from './analysisMode';
import type { GeneratedParlay } from './api/picksApi';

/** Server-persisted generation entry (from generations table, picks JSONB column) */
export interface PersistedGenerationEntry {
  id: string; // generationId
  timestamp: number; // epoch ms (from DB createdAt)
  picks: GeneratedParlay[]; // the generated parlays; [] when failed
  /** True when the run wrote no picks. Such an entry is shown but cannot be loaded. */
  failed?: boolean;
  /** Human-readable reason, derived from status server-side. Null when not failed. */
  failureReason?: string | null;
  analysisMode: AnalysisMode; // 'standard' | 'detailed' | 'premium'
  sportsbook: string; // e.g. 'draftkings'
  gameIds: string[]; // Odds API game IDs selected
  gameCount: number; // gameIds.length (for display convenience)
  cached: boolean; // whether this was a cached result
  leagueKey: string; // League used for this generation (e.g., 'basketball_nba')
  status: string; // 'success' | 'truncated' | 'empty' | 'error' | 'cancelled'
  cost: string; // USD cost of the AI call
  durationMs: number; // AI call duration
  model: string; // AI model used
  provider: string; // AI provider used
}

/** Response from GET /api/generation-history */
export interface GenerationHistoryResponse {
  entries: PersistedGenerationEntry[];
}

/**
 * A live response stamps each parlay with its sportsbook after the picks are saved, so stored
 * picks carry none, and a restored parlay reached the builder as an 'Unknown' book.
 */
export function withParlaySportsbook<T>(parlays: T[], sportsbook: string | null | undefined): T[] {
  if (!sportsbook) return parlays;
  return parlays.map((parlay) =>
    parlay && typeof parlay === 'object' && !('sportsbook' in parlay && parlay.sportsbook)
      ? { ...parlay, sportsbook }
      : parlay,
  );
}
